import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { FlxKeyboardDirective, FlxUITextComponent } from '@flowx/angular-ui-toolkit'
import {
  DecorativeAccessibilityProps,
  ProcessActionTriggerEvent,
  ResolvedAction,
  TextTemplateConfig,
  getActionsByTemplateConfigAndTrigger$,
} from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../pipes/flx-localize.pipe'
import { FlxMarkdownPipe } from '../../pipes/flx-markdown.pipe'
import { FlxActionService } from '../../services/flx-action.service'
import { getBoolExpressionSignal, getComputedValueSignal, getDynamicPropSignal } from '../helpers'

@Component({
  selector: 'flx-text',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [
    FlxUITextComponent,
    FlxLocalizePipe,
    FlxMarkdownPipe,
    FlxThemeModule,
    FlxStyleModule,
    FlxKeyboardDirective,
  ],
  templateUrl: './flx-text.component.html',
  styleUrl: './flx-text.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxTextComponent implements OnInit, AfterViewInit {
  #injector = inject(Injector)
  #actionService = inject(FlxActionService)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)
  #destroyRef = inject(DestroyRef)

  // actions for the CLICK trigger – kept in sync with the process-actions store
  // so they arrive even when the node's details resolve after this component mounts.
  #clickActions = signal<ResolvedAction[]>([])

  templateConfig = input.required<TextTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)
  accessibility = computed(
    (): DecorativeAccessibilityProps | null => this.flowxProps()?.accessibility
  )

  evaluatedConditionalStyles = signal([])
  conditionalStyles = computed(() =>
    this.evaluatedConditionalStyles()
      .filter(({ expression }) => expression())
      .map(({ overrides }) => overrides)
  )

  // expressions
  isHidden: Signal<boolean>
  ariaHidden: Signal<boolean>

  hasClickAction = computed(() => this.#clickActions().length > 0)

  // dynamic props
  text: Signal<string>

  ngOnInit(): void {
    getActionsByTemplateConfigAndTrigger$(this.templateConfig(), ProcessActionTriggerEvent.CLICK)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#clickActions.set(actions))

    runInInjectionContext(this.#injector, () => {
      const { text, isComputedText, hide } = this.flowxProps()
      const { conditionals } = this.style()

      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })
      this.ariaHidden = computed(() => this.accessibility()?.decorative)
      this.text = isComputedText
        ? getComputedValueSignal(text, this.templateConfig())
        : getDynamicPropSignal(text, this.templateConfig())
      if (Array.isArray(conditionals)) {
        this.evaluatedConditionalStyles.set(
          conditionals.map(({ condition, overrides }, index) => ({
            expression: getBoolExpressionSignal(condition, this.templateConfig(), {
              logType: 'conditional',
              conditionIndex: index,
            }),
            overrides,
          }))
        )
      }
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }

  async onClick(): Promise<void> {
    if (!this.hasClickAction()) {
      return
    }
    await this.#actionService.runActions(this.#clickActions(), this.templateConfig())
  }
}
